import type { ReactNode } from 'react';
import { DatePicker } from '@/components/ui/DatePicker';

export function RadioOption({
  name,
  label,
  checked,
  onChange,
  testId,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: () => void;
  testId?: string;
}) {
  return (
    <label
      className={`group flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition ${
        checked
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-slate-50'
      }`}
      data-testid={testId ? `${testId}-label` : undefined}
    >
      <input
        type="radio"
        name={name}
        className="hidden"
        checked={checked}
        onChange={onChange}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <div
        className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white group-hover:border-blue-300'}`}
      >
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </div>
      <span className="font-medium">{label}</span>
    </label>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  testId?: string;
}) {
  return (
    <label
      className="group flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:bg-slate-50"
      data-testid={testId ? `${testId}-label` : undefined}
    >
      <input
        type="checkbox"
        className="hidden"
        checked={checked}
        onChange={onChange}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <div
        className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white group-hover:border-blue-300'}`}
      >
        {checked && (
          <svg
            className="h-3 w-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      <span>{label}</span>
    </label>
  );
}

export function SubFieldGroup({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3">
      {children}
    </div>
  );
}

export function AdjustmentSelect({
  value,
  onChange,
  options,
  placeholder = 'Select Reason',
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
  placeholder?: string;
}) {
  return (
    <div>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      >
        <option value="">{placeholder}</option>
        {options.map(([optionValue, label]) => (
          <option key={optionValue} value={optionValue}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      />
    </label>
  );
}

export function ChargebackAmountField({
  label,
  value,
  onChange,
  onBlur,
  showMinusPrefix = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  showMinusPrefix?: boolean;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <div className="relative">
        {showMinusPrefix ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-900">
            -
          </span>
        ) : null}
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          className={`h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${showMinusPrefix ? 'pl-6' : ''}`}
        />
      </div>
    </label>
  );
}

export function DateField({
  label,
  value,
  onChange,
  popperPlacement = 'bottom-start',
  maxDate,
  allowFutureDates = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  popperPlacement?: 'bottom-start' | 'bottom-end';
  maxDate?: Date;
  allowFutureDates?: boolean;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <DatePicker
        value={value}
        onChange={onChange}
        placeholder="MM/DD/YYYY"
        allowFutureDates={allowFutureDates}
        maxDate={maxDate}
        showHelperText={false}
        popperPlacement={popperPlacement}
        popperFixed
        calendarPortalId="liberty-payment-adjustment-datepicker"
        className="h-10 rounded-xl border-slate-200 bg-white px-3 pr-10 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      />
    </label>
  );
}

export function ExternalRefundFields({
  checked,
  onCheckedChange,
  paymentSource,
  onPaymentSourceChange,
  onCheckNumberChange,
  testIdPrefix,
}: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  paymentSource: string;
  onPaymentSourceChange: (value: string) => void;
  onCheckNumberChange: (value: string) => void;
  testIdPrefix: string;
}) {
  const toggleExternalRefund = () => {
    const nextChecked = !checked;
    onCheckedChange(nextChecked);
    if (!nextChecked || paymentSource === 'CARDCONNECT') {
      onPaymentSourceChange('');
      onCheckNumberChange('');
    }
  };

  return (
    <div className="space-y-3">
      <Checkbox
        label="External Refund"
        checked={checked}
        onChange={toggleExternalRefund}
        testId={`${testIdPrefix}-external-refund`}
      />
    </div>
  );
}

export function Row({
  label,
  value,
  isBold = false,
}: {
  label: string;
  value: string;
  isBold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span
        className={`text-sm ${isBold ? 'font-bold' : ''} text-slate-900`}
      >
        {value}
      </span>
    </div>
  );
}

export function EditableAmountRow({
  label,
  value,
  onChange,
  onBlur,
  hasError,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  hasError: boolean;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-[1fr_9rem] items-center gap-4">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        className={`h-10 w-full rounded-xl border bg-white px-3 text-left text-sm text-slate-900 outline-none transition focus:ring-2 disabled:bg-slate-50 disabled:text-slate-400 ${
          hasError
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
            : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
        }`}
      />
    </div>
  );
}
